import { useEffect, useState } from 'react';

// Moves the sun along solar cameras' cards as the page scrolls (Home/Item.jsx):
// it shines on the row of cards in the middle of the screen, one card on
// phones, all of them side by side on larger screens. It stays on a row while
// the row's middle is near the screen's middle and fades over to the next
// halfway between them, setting each card's --sunlight, 0 to 1, as it goes.
// Returns whether each card is in the row it's on, none until it has measured
// them.
export default function useSunlight(cardsRef, enabled) {
  const [lit, setLit] = useState([]);

  useEffect(() => {
    if (!enabled) return;
    let frame = 0;

    const update = () => {
      frame = 0;
      const cards = cardsRef.current;
      if (!cards.length || !cards.every(Boolean)) return;

      const rects = cards.map((card) => card.getBoundingClientRect());
      const tops = [...new Set(rects.map((rect) => Math.round(rect.top)))];
      const rowOf = rects.map((rect) => tops.indexOf(Math.round(rect.top)));
      const stops = tops.map((top, row) => {
        const rect = rects[rowOf.indexOf(row)];
        return rect.top + rect.height / 2;
      });

      // Where the sun is: a row's index, or between two rows' indexes.
      const middle = window.innerHeight / 2;
      let sun = stops.length - 1;
      for (let i = 0; i < stops.length - 1; i++) {
        if (middle < stops[i + 1]) {
          sun = i + Math.max(0, (middle - stops[i]) / (stops[i + 1] - stops[i]));
          break;
        }
      }

      // Full within a quarter of the way to the next row, then fading out by
      // three quarters, while the next row's fades in.
      cards.forEach((card, i) => {
        const light = Math.min(1, Math.max(0, (0.75 - Math.abs(sun - rowOf[i])) / 0.5));
        card.style.setProperty('--sunlight', light.toFixed(3));
      });
      const next = rowOf.map((row) => row === Math.round(sun));
      setLit((prev) => (prev.length === next.length && prev.every((on, i) => on === next[i]) ? prev : next));
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [cardsRef, enabled]);

  return lit;
}
